import React, { useState, useEffect, useMemo } from 'react';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import api from '../../services/api';
import { Drawer } from '../../components/ui/Drawer';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface PayRate {
  recno: number;
  origzone: string;
  destzone: string;
  origcode: number;
  destcode: number;
  legtype: string;
  fueltype: string;
  rate: number;
  ratetype: string;
  effectivedate: string | null;
  expirationdate: string | null;
  paystatus: string;
  legmiles: number;
  direction: 'forward' | 'reverse';
  count_30: number;
  count_60: number;
  count_90: number;
  last_pro_date: string | null;
}

interface TravelTimeRow {
  id: number;
  origcode: number;
  destcode: number;
  origin_name: string;
  origin_zone: string;
  destination_name: string;
  destination_zone: string;
  miles: number;
  duration_minutes: number;
  pay_rates: PayRate[];
}

type SortColumn = 'route' | 'rate' | 'ppm' | 'fueltype' | 'legtype' | 'paystatus' | 'effectivedate' | 'recno' | 'count_30' | 'count_60' | 'count_90';
type SortDirection = 'asc' | 'desc';

const LaneDetails: React.FC = () => {
  const [data, setData] = useState<TravelTimeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TravelTimeRow | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/travel-times/list');
      setData(response.data.data || []);
    } catch (error) {
      console.error('Error loading travel times:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handlePayRatesClick = (row: TravelTimeRow) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  const columns = useMemo<ColumnDef<TravelTimeRow>[]>(() => [
    {
      accessorKey: 'origin_name',
      header: 'Origin',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'origin_zone',
      header: 'Orig Zone',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'destination_name',
      header: 'Destination',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'destination_zone',
      header: 'Dest Zone',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'miles',
      header: 'Miles',
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue() as number;
        return value ? value.toFixed(2) : '-';
      },
    },
    {
      accessorKey: 'duration_minutes',
      header: 'Duration (min)',
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue() as number;
        if (!value || value < 0) return '-';
        return value;
      },
    },
    {
      id: 'min_rate',
      header: 'Min Rate',
      enableSorting: true,
      accessorFn: (row) => {
        if (!row.pay_rates?.length) return null;
        return Math.min(...row.pay_rates.map(pr => pr.rate));
      },
      cell: (info) => {
        const value = info.getValue() as number | null;
        if (value === null) return <span className="text-gray-400">-</span>;
        return <span className="text-green-600 dark:text-green-400">${value.toFixed(2)}</span>;
      },
    },
    {
      id: 'max_rate',
      header: 'Max Rate',
      enableSorting: true,
      accessorFn: (row) => {
        if (!row.pay_rates?.length) return null;
        return Math.max(...row.pay_rates.map(pr => pr.rate));
      },
      cell: (info) => {
        const value = info.getValue() as number | null;
        if (value === null) return <span className="text-gray-400">-</span>;
        return <span className="text-green-600 dark:text-green-400">${value.toFixed(2)}</span>;
      },
    },
    {
      id: 'min_price_per_mile',
      header: 'Min $/Mi',
      enableSorting: true,
      accessorFn: (row) => {
        if (!row.pay_rates?.length || !row.miles) return null;
        const minRate = Math.min(...row.pay_rates.map(pr => pr.rate));
        return minRate / row.miles;
      },
      cell: (info) => {
        const value = info.getValue() as number | null;
        if (value === null) return <span className="text-gray-400">-</span>;
        return <span className="text-blue-600 dark:text-blue-400">${value.toFixed(2)}</span>;
      },
    },
    {
      id: 'max_price_per_mile',
      header: 'Max $/Mi',
      enableSorting: true,
      accessorFn: (row) => {
        if (!row.pay_rates?.length || !row.miles) return null;
        const maxRate = Math.max(...row.pay_rates.map(pr => pr.rate));
        return maxRate / row.miles;
      },
      cell: (info) => {
        const value = info.getValue() as number | null;
        if (value === null) return <span className="text-gray-400">-</span>;
        return <span className="text-blue-600 dark:text-blue-400">${value.toFixed(2)}</span>;
      },
    },
    {
      id: 'last_pro_date',
      header: 'Last Pro',
      enableSorting: true,
      accessorFn: (row) => {
        if (!row.pay_rates?.length) return null;
        const dates = row.pay_rates
          .map(pr => pr.last_pro_date)
          .filter((d): d is string => d !== null);
        if (!dates.length) return null;
        return dates.sort().reverse()[0]; // Get most recent date
      },
      cell: (info) => {
        const value = info.getValue() as string | null;
        if (!value) return <span className="text-gray-400">-</span>;
        return <span className="text-orange-600 dark:text-orange-400">{value}</span>;
      },
    },
    {
      id: 'recent_usage',
      header: 'Recent',
      enableSorting: true,
      accessorFn: (row) => {
        if (!row.pay_rates?.length) return 0;
        return row.pay_rates.reduce((sum, pr) => sum + (pr.count_90 || 0), 0);
      },
      cell: (info) => {
        const value = info.getValue() as number;
        if (value === 0) return <span className="text-gray-400">-</span>;
        return <span className="text-purple-600 dark:text-purple-400">{value}</span>;
      },
    },
    {
      id: 'pay_rates_count',
      header: 'Rates',
      enableSorting: true,
      accessorFn: (row) => row.pay_rates?.length || 0,
      cell: (info) => {
        const row = info.row.original;
        const count = row.pay_rates?.length || 0;
        if (count === 0) return <span className="text-gray-400">-</span>;

        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePayRatesClick(row);
            }}
            className="px-2 py-1 bg-info text-info-fg rounded text-sm hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors cursor-pointer"
          >
            {count}
          </button>
        );
      },
    },
  ], []);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Helper to build route display for a pay rate
  const getRouteDisplay = (pr: PayRate, row: TravelTimeRow) => {
    const isDestZoneBased = pr.destcode === 0;
    let fromLabel: string;
    let toLabel: string;

    if (pr.direction === 'forward') {
      fromLabel = row.origin_name || 'Origin';
      toLabel = isDestZoneBased ? `Dest Zone: ${pr.destzone}` : (row.destination_name || 'Destination');
    } else {
      fromLabel = row.destination_name || 'Destination';
      toLabel = isDestZoneBased ? `Dest Zone: ${pr.destzone}` : (row.origin_name || 'Origin');
    }

    return { fromLabel, toLabel, isDestZoneBased };
  };

  return (
    <div className="p-6">
      <TanTable
        data={data}
        columns={columns}
        loading={loading}
        cardTitle="Lane Details"
        settingsKey="lane-details"
        fieldName="lane-details-table"
        enableColumnFilters={true}
        enableExcelExport={true}
        enableSearch={true}
        enableReset={true}
        enableShowHideCols={true}
        enableRowsPerPage={false}
        rowsPerPageOptions={[100]}
        enableZebraStriping={true}
      />

      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? `Pay Rates: ${selectedRow.origin_name} ↔ ${selectedRow.destination_name}` : 'Pay Rates'}
        position="right"
        width="75vw"
      >
        {selectedRow && selectedRow.pay_rates && (
          <div className="-mx-2">
            <div className="text-xs text-muted mb-2 px-2">
              {selectedRow.pay_rates.length} rate{selectedRow.pay_rates.length !== 1 ? 's' : ''} found
            </div>

            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  {[
                    { key: 'route', label: 'Route' },
                    { key: 'rate', label: 'Rate' },
                    { key: 'ppm', label: '$/Mi' },
                    { key: 'fueltype', label: 'Fuel' },
                    { key: 'legtype', label: 'Leg Type' },
                    { key: 'paystatus', label: 'Status' },
                    { key: 'effectivedate', label: 'Effective' },
                    { key: 'count_30', label: '30d' },
                    { key: 'count_60', label: '60d' },
                    { key: 'count_90', label: '90d' },
                    { key: 'recno', label: 'Recno' },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      className="text-left py-1 px-2 font-medium cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                      onClick={() => handleSort(key as SortColumn)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        {sortColumn === key ? (
                          sortDirection === 'asc'
                            ? <ChevronUpIcon className="w-3 h-3" />
                            : <ChevronDownIcon className="w-3 h-3" />
                        ) : null}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...selectedRow.pay_rates]
                  .sort((a, b) => {
                    // Default sort: LegType asc, then Effective Date desc (newest first)
                    if (!sortColumn) {
                      const legCompare = (a.legtype || '').localeCompare(b.legtype || '');
                      if (legCompare !== 0) return legCompare;
                      // Effective date desc (newest first)
                      return (b.effectivedate || '').localeCompare(a.effectivedate || '');
                    }

                    let aVal: string | number = '';
                    let bVal: string | number = '';

                    switch (sortColumn) {
                      case 'route':
                        aVal = a.direction;
                        bVal = b.direction;
                        break;
                      case 'rate':
                        aVal = a.rate;
                        bVal = b.rate;
                        break;
                      case 'ppm':
                        aVal = selectedRow.miles ? a.rate / selectedRow.miles : 0;
                        bVal = selectedRow.miles ? b.rate / selectedRow.miles : 0;
                        break;
                      case 'fueltype':
                        aVal = a.fueltype || '';
                        bVal = b.fueltype || '';
                        break;
                      case 'legtype':
                        aVal = a.legtype || '';
                        bVal = b.legtype || '';
                        break;
                      case 'paystatus':
                        aVal = a.paystatus || '';
                        bVal = b.paystatus || '';
                        break;
                      case 'effectivedate':
                        aVal = a.effectivedate || '';
                        bVal = b.effectivedate || '';
                        break;
                      case 'recno':
                        aVal = a.recno;
                        bVal = b.recno;
                        break;
                      case 'count_30':
                        aVal = a.count_30;
                        bVal = b.count_30;
                        break;
                      case 'count_60':
                        aVal = a.count_60;
                        bVal = b.count_60;
                        break;
                      case 'count_90':
                        aVal = a.count_90;
                        bVal = b.count_90;
                        break;
                    }

                    if (typeof aVal === 'number' && typeof bVal === 'number') {
                      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
                    }
                    const comparison = String(aVal).localeCompare(String(bVal));
                    return sortDirection === 'asc' ? comparison : -comparison;
                  })
                  .map((pr, idx) => {
                  const { fromLabel, toLabel, isDestZoneBased } = getRouteDisplay(pr, selectedRow);

                  return (
                    <tr
                      key={pr.recno || idx}
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <td className="py-1 px-2">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-xs ${
                          pr.direction === 'forward'
                            ? 'bg-ok text-ok-fg'
                            : 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
                        }`}>
                          {fromLabel} → {toLabel}
                          {isDestZoneBased && <span className="ml-1 opacity-70">(Z)</span>}
                        </span>
                      </td>
                      <td className="py-1 px-2 font-semibold text-green-600 dark:text-green-400">
                        ${pr.rate.toFixed(2)}
                        <span className="ml-1 font-normal text-gray-400 text-xs">({pr.ratetype})</span>
                      </td>
                      <td className="py-1 px-2 text-blue-600 dark:text-blue-400">
                        {selectedRow.miles ? `$${(pr.rate / selectedRow.miles).toFixed(2)}` : '-'}
                      </td>
                      <td className="py-1 px-2">{pr.fueltype || '-'}</td>
                      <td className="py-1 px-2">{pr.legtype || '-'}</td>
                      <td className="py-1 px-2">{pr.paystatus || '-'}</td>
                      <td className="py-1 px-2">{pr.effectivedate || '-'}</td>
                      <td className="py-1 px-2 text-center">{pr.count_30}</td>
                      <td className="py-1 px-2 text-center">{pr.count_60}</td>
                      <td className="py-1 px-2 text-center">{pr.count_90}</td>
                      <td className="py-1 px-2">{pr.recno}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default LaneDetails;
